import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import type { AppDispatch } from "@/app/store";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { invalidateAccountVerification } from "@/modules/verification/services/accountVerification.memo";
import { invalidateCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { isProviderBalanceShort, normalizePaymentError } from "@/shared/services/payments/paymentErrors";

import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  resolveColRail,
  resolveRuntimeCorridor,
} from "../constants/dispersions.constants";
import {
  PaymentsPayoutService,
  buildArgentinaCbuCvuPayoutPayload,
  buildBrazilPixPayoutPayload,
  BrebKeyType,
  buildColombiaBrebPayoutPayload,
  buildColombiaTransfiyaPayoutPayload,
  type ColombianDocumentType,
  type TransfiyaAccountType,
  buildMexicoSpeiPayoutPayload,
  newIdempotencyKey,
  PayoutStatus,
  resolveCbuCvuKind,
} from "../services/paymentsPayout.service";
import type { NativeQuote } from "../services/paymentsQuotes.service";
import { setFormSubmitting, setFormSubmitError } from "../store/dispersionsSlice";
import type { DispersionFormData } from "../types/dispersions-api.types";
import { advancePayoutStatus } from "../utils/payoutStatusProgress";
import { classifyQuoteConflict } from "../utils/quoteConflict";
import { resolveRecipientDisplay } from "../utils/recipientDisplay";
import { useBrebKeyResolution } from "./useBrebKeyResolution";
import { useDispersions } from "./useDispersions";
import { usePayoutLiveStatus } from "./usePayoutLiveStatus";

/** What the result screen shows about the created payout. */
export interface DispersionResponse {
  id: string;
  status: PayoutStatus;
  createdAt: string;
}

/** Snapshot shown by the success screen — captured BEFORE the form resets. */
export interface DispersionSuccessData {
  dispersionResponse: DispersionResponse;
  formData: DispersionFormData;
  /** The redeemed quote, verbatim — it is already frozen and immutable. */
  quoteData?: NativeQuote;
  /**
   * Who the money went to, resolved at submit time.
   *
   * Bre-B stopped asking for a name, so the result screen cannot read one off `formData` any more — it would
   * print "The payment to — ". This is a snapshot on purpose: re-asking the directory after the payout exists
   * could only contradict the identity the operator approved.
   */
  recipientDisplay: string;
  /**
   * Which amount the operator actually typed — the other side was filled in by the quote.
   *
   * Only the receipt's repeat actions read it, and they need it: the redeemed quote is spent, so repeating
   * the send has to price a new one, and pricing it from the wrong side silently changes the deal. On a
   * fixed-receive send, re-quoting by the origin amount would hand the recipient a different number than the
   * one they were promised the first time.
   */
  pricedFrom: "origin" | "destination" | null;
}

/**
 * `"success"` means the CREATE succeeded, not that the money arrived — the outcome inside it is driven by the
 * payout's live status.
 */
export type DispersionResultPhase = "idle" | "processing" | "success" | "error";

/**
 * How the error screen should behave — which is a question about MONEY, not about copy.
 *
 * - `generic` — the attempt did not move anything. Retrying is safe.
 * - `quote_consumed` — the quote was spent, so a payout may exist. Retrying could pay twice; the operator has
 *   to look at the history first.
 * - `unconfirmed` — a conflict we could not resolve. Treated like `quote_consumed`, because the safe assumption
 *   when money might have moved is that it did.
 * - `verification_required` — the organization's own verification is not VERIFIED, so the runtime refused to
 *   create the payout (403 `organization_verification_required`). Nothing moved, and retrying cannot succeed
 *   until the account is verified: the remedy is the onboarding, not the wizard.
 */
export type DispersionErrorKind = "generic" | "quote_consumed" | "unconfirmed" | "verification_required";

/**
 * Submission orchestration for the dispersion wizard, extracted verbatim from `DispersionsListPage` so the
 * page holds no API calls (project rule: services + thunks/hooks only). Every corridor is executed by the
 * NestJS payments runtime (the legacy PHP proxy and its 2FA modal are gone). It owns: - the per-corridor
 * payout payload, - the payout IDEMPOTENCY KEY lifecycle (one key per operation, reused across retries so a
 * lost response never double-debits; regenerated when the user edits data or after a terminal FAILED), - the
 * result phases (processing/success/error) and the success-screen snapshot.
 *
 * `onSuccessCleanup` runs right after a successful submit, for page-owned state (the /app/wallets origin
 * lock) that must be released alongside the form reset.
 */
export interface UseSubmitDispersionOptions {
  onSuccessCleanup: () => void;
  /**
   * Called instead of failing when the quote is (or has just been found) expired. The page owns the re-quote
   * modal, so the decision is handed back to it — an expired price is a "get a new one" moment, not an error
   * screen.
   */
  onQuoteExpired?: () => void;
}

export function useSubmitDispersion({ onSuccessCleanup, onQuoteExpired }: UseSubmitDispersionOptions) {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslations("dispersions");
  const { user, isInternalUser } = useAuth();
  const { formData, quoteData, lastChangedAmountField, resetDispersionForm, clearQuoteData } =
    useDispersions();

  // The recipient's name as it will be reported after the fact. Bre-B has none in `formData` — the
  // directory owns it — so it is captured here, while the key that produced it is still the one on screen.
  const brebKeyTypeRaw = String(formData.recipientFields?.breb_key_type ?? "").toUpperCase();
  const brebResolution = useBrebKeyResolution(
    (Object.values(BrebKeyType) as string[]).includes(brebKeyTypeRaw)
      ? (brebKeyTypeRaw as BrebKeyType)
      : BrebKeyType.PHONE,
    String(formData.recipientFields?.breb_key_value ?? ""),
    resolveRuntimeCorridor(formData.destination) === "COL" &&
      resolveColRail(formData.recipientFields) !== "TRANSFIYA",
  );

  const [resultPhase, setResultPhase] = useState<DispersionResultPhase>("idle");
  const [showResultDetails, setShowResultDetails] = useState(false);
  const [successModalData, setSuccessModalData] = useState<DispersionSuccessData | null>(null);
  const [errorKind, setErrorKind] = useState<DispersionErrorKind>("generic");

  // The create response only says what was true at that instant — for every rail
  // the real outcome arrives later, on the provider webhook. Keep tracking the
  // payout while the result screen is open (socket + poll) so the screen states
  // "in progress" until the network actually confirms it.
  const liveStatus = usePayoutLiveStatus(
    resultPhase === "success" ? (successModalData?.dispersionResponse.id ?? null) : null,
  );
  // The live channel only upgrades the create response: a late reading older than it never wins.
  const payoutStatus: PayoutStatus | null = advancePayoutStatus(
    successModalData?.dispersionResponse.status ?? null,
    liveStatus,
  );

  // Payout idempotency key. Generated once per operation and REUSED across
  // retries: if the create reached the backend but the response was lost
  // (timeout), the retry returns the existing payout instead of creating
  // (and reserving funds for) a duplicate. Regenerated when the user goes
  // back to edit data (a different operation).
  const payoutIdempotencyKeyRef = useRef<string | null>(null);
  // Quote the current idempotency key was minted for — a new quote mints a new key.
  const lastQuoteIdRef = useRef<string | null>(null);

  /** The next submit is a DIFFERENT operation (user edited data / started over). */
  const resetIdempotencyKey = useCallback(() => {
    payoutIdempotencyKeyRef.current = null;
  }, []);

  const submitDispersion = async () => {
    dispatch(setFormSubmitting(true));
    dispatch(setFormSubmitError(null));
    setResultPhase("processing");
    setShowResultDetails(false);

    try {
      // Canonical corridor detection — single source of the spelling tolerance (constants). The wizard only
      // offers runtime corridors (`PAYOUT_WIZARD_COUNTRIES`), so null can only mean a stale deep link or
      // persisted form state naming a retired legacy corridor.
      const runtimeCorridor = resolveRuntimeCorridor(formData.destination);
      if (runtimeCorridor === null) {
        throw new Error(t("error_create_failed"));
      }

      // Create IS the full flow: the backend reserves the funds AND auto-dispatches
      // to the provider in the same request (any user type), so the response carries
      // the payout's real state — PROCESSING/PAID (sent), FAILED (rejected, funds
      // already released) or CREATED (pre-dispatch config failure; ops retries via
      // staff dispatch). Only the payload builder is per-corridor.
      const organizationId = user?.organization_id ?? user?.organization?.id;
      if (!organizationId) {
        throw new Error(t("error_no_organization"));
      }
      // The quote is mandatory: it carries the conversion, the frozen rate and
      // both fees, and the backend redeems it. Guarding here keeps a doomed POST
      // from ever leaving the browser. Step 3 now blocks the button on the same
      // predicate (`resolvePayoutQuoteBlock`), so reaching these is the exception.
      //
      // There is no CONSUMED check: the store only ever holds a quote returned by
      // `POST /quotes`, which is always ACTIVE, so that branch was unreachable.
      if (!quoteData) throw new Error(t("error_quote_required"));
      if (quoteData.service !== "payout") throw new Error(t("error_quote_invalid"));
      // Expiry is not an error screen: the user just needs a fresh price, and the
      // key must be released because a new quote is a new operation.
      if (Date.parse(quoteData.expires_at) <= Date.now()) {
        payoutIdempotencyKeyRef.current = null;
        onQuoteExpired?.();
        return;
      }
      // A quote left over from another corridor would redeem for the wrong currency.
      if (quoteData.currency.to !== CURRENCY_BY_RUNTIME_CORRIDOR[runtimeCorridor]) {
        throw new Error(t("error_quote_invalid"));
      }

      // `amounts.receive` is the CONVERTED GROSS the backend applies its commission
      // to — not `payout_net`, which is what the beneficiary ends up with.
      const receiveAmount = quoteData.amounts.receive;
      const quoteId = quoteData.id;

      // Reuse the same key across retries of THIS operation (see the ref) — but a
      // DIFFERENT quote is a different operation (e.g. re-quoted after switching the
      // Colombia rail): reusing the key there would trip the server-side idempotency
      // cache with a mismatched body.
      if (!payoutIdempotencyKeyRef.current || lastQuoteIdRef.current !== quoteId) {
        payoutIdempotencyKeyRef.current = newIdempotencyKey();
      }
      lastQuoteIdRef.current = quoteId;

      const recipient = formData.recipientFields;
      const concept = recipient?.description ? String(recipient.description) : undefined;
      const payoutPayload =
        runtimeCorridor === "BRA"
          ? buildBrazilPixPayoutPayload({
              organizationId,
              receiveAmount,
              quoteId,
              pixKeyType: recipient?.pix_type as number | string,
              pixKeyValue: String(recipient?.pix_key ?? ""),
              beneficiaryName: recipient?.beneficiary ? String(recipient.beneficiary) : undefined,
              concept,
              idempotencyKey: payoutIdempotencyKeyRef.current,
            })
          : runtimeCorridor === "MEX"
            ? buildMexicoSpeiPayoutPayload({
                organizationId,
                receiveAmount,
                quoteId,
                clabe: String(recipient?.clabe ?? ""),
                beneficiaryName: String(recipient?.beneficiary ?? ""),
                concept,
                idempotencyKey: payoutIdempotencyKeyRef.current,
              })
            : runtimeCorridor === "ARG"
              ? buildArgentinaCbuCvuPayoutPayload({
                  organizationId,
                  receiveAmount,
                  quoteId,
                  // The shared resolver owns the CVU default. A value that is neither falls through as
                  // itself, so the builder throws naming it instead of quietly paying the other rail.
                  accountIdType:
                    resolveCbuCvuKind(recipient?.account_type) ?? String(recipient?.account_type ?? ""),
                  accountIdValue: String(recipient?.account_number ?? ""),
                  cuitCuil: recipient?.cuit ? String(recipient.cuit) : undefined,
                  beneficiaryName: recipient?.beneficiary ? String(recipient.beneficiary) : undefined,
                  concept,
                  idempotencyKey: payoutIdempotencyKeyRef.current,
                })
              : resolveColRail(recipient) === "TRANSFIYA"
                ? buildColombiaTransfiyaPayoutPayload({
                    organizationId,
                    receiveAmount,
                    quoteId,
                    accountType: String(recipient?.transfiya_account_type ?? "svgs") as TransfiyaAccountType,
                    accountNumber: String(recipient?.transfiya_account_number ?? ""),
                    bankCode: String(recipient?.transfiya_bank_code ?? ""),
                    documentType: String(
                      recipient?.transfiya_document_type ?? "CC",
                    ).toUpperCase() as ColombianDocumentType,
                    documentNumber: String(recipient?.transfiya_document_number ?? ""),
                    beneficiaryName: String(recipient?.beneficiary ?? ""),
                    concept,
                    idempotencyKey: payoutIdempotencyKeyRef.current,
                  })
                : buildColombiaBrebPayoutPayload({
                    organizationId,
                    receiveAmount,
                    quoteId,
                    brebKeyType: String(recipient?.breb_key_type ?? "PHONE"),
                    brebKeyValue: String(recipient?.breb_key_value ?? ""),
                    beneficiaryName: recipient?.beneficiary ? String(recipient.beneficiary) : undefined,
                    concept,
                    idempotencyKey: payoutIdempotencyKeyRef.current,
                  });

      const { data: payout } = await PaymentsPayoutService.createPayout(payoutPayload);

      // Create answers 201 with the payout ROW even when the provider
      // rejected the payment (status FAILED, reserved funds released) — a
      // terminal failure, never a success. The failed payout can't be
      // re-dispatched, so drop the idempotency key: a retry is a NEW payout.
      const finalStatus = payout.status;
      if (finalStatus === PayoutStatus.FAILED) {
        payoutIdempotencyKeyRef.current = null;
        // The backend claimed the quote before the provider rejected, so it is
        // spent: a retry needs a fresh price, not the stale one on screen.
        clearQuoteData();
        throw new Error(t("error_payment_rejected"));
      }

      const runtimeFormDataCopy: DispersionFormData = {
        origin_currency: formData.origin_currency,
        destination: formData.destination,
        amount_origin: formData.amount_origin,
        amount_destination: formData.amount_destination,
        recipientFields: { ...formData.recipientFields },
        recipientMode: formData.recipientMode,
        selectedClientId: formData.selectedClientId,
        selectedClientAccountId: formData.selectedClientAccountId,
        selectedClientAccountMethod: formData.selectedClientAccountMethod,
      };

      setSuccessModalData({
        recipientDisplay: resolveRecipientDisplay({
          typedName: String(formData.recipientFields?.beneficiary ?? ""),
          resolvedHolder: brebResolution.status === "resolved" ? brebResolution.data.holder_name : null,
          brebKeyType: String(formData.recipientFields?.breb_key_type ?? ""),
          brebKeyValue: String(formData.recipientFields?.breb_key_value ?? ""),
        }),
        dispersionResponse: {
          id: payout.id,
          status: finalStatus,
          createdAt: payout.created_at,
        },
        formData: runtimeFormDataCopy,
        quoteData: quoteData ?? undefined,
        pricedFrom: lastChangedAmountField,
      });

      resetDispersionForm();
      onSuccessCleanup();
      payoutIdempotencyKeyRef.current = null;
      setResultPhase("success");
    } catch (error: unknown) {
      const normalized = normalizePaymentError(error, { quoteExpiresAt: quoteData?.expires_at });

      // The quote lapsed while the request was in flight. Same treatment as the pre-flight guard: a fresh
      // price, not an error screen.
      if (normalized.kind === "quote_expired") {
        payoutIdempotencyKeyRef.current = null;
        clearQuoteData();
        onQuoteExpired?.();
        return;
      }

      // The organization's own verification is not VERIFIED (403 `organization_verification_required`). Nothing
      // moved and no retry can succeed until the account is verified, so the wizard shows the gate on its next
      // mount — drop the memoized verdict it reads.
      if (normalized.kind === "verification_required") {
        payoutIdempotencyKeyRef.current = null;
        invalidateAccountVerification();
        setErrorKind("verification_required");
        dispatch(setFormSubmitError(t("error_verification_required")));
        setResultPhase("error");
        throw error;
      }

      // A 409 here is SIX different things, and they do not share a remedy. `quote_already_used` may mean a
      // payout exists; `quote_route_mismatch` is thrown before anything is inserted or reserved and just
      // wants a new price; the idempotency conflicts moved nothing either. The response cannot tell them
      // apart — no `code`, and the sentence is localised — so we ask the one endpoint that answers
      // authoritatively instead of inferring from the status.
      // One 409 does carry a `code`: the rail cannot pay this amount right now (`provider_balance_insufficient`).
      // It is refused before anything is created, so the quote was not spent and the backend's sentence, which
      // names the rail to use instead, is the whole answer. The corridor memo is dropped for the next rail pick.
      const balanceShort = isProviderBalanceShort(error);
      if (balanceShort) invalidateCorridors();

      let kind: DispersionErrorKind = "generic";
      if (HttpApiService.isConflictError(error) && quoteData && !balanceShort) {
        kind = await classifyQuoteConflict(
          quoteData.id,
          // Members are pinned server-side and must NOT send it; INTERNAL staff must, or the read is a 400.
          isInternalUser ? (user?.organization_id ?? user?.organization?.id) : undefined,
        );
        // Whatever the conflict was, this quote is finished: either it was spent, or it was priced against a
        // route that no longer exists. Dropping it sends "Adjust data" back to a wizard that will demand a
        // fresh price instead of walking into the same 409 again.
        clearQuoteData();
      }

      setErrorKind(kind);
      dispatch(
        setFormSubmitError(
          kind === "generic"
            ? normalized.messages.join(" ") || t("error_create_failed")
            : t(kind === "quote_consumed" ? "error_quote_already_used" : "error_payout_unconfirmed"),
        ),
      );
      setResultPhase("error");
      throw error;
    } finally {
      dispatch(setFormSubmitting(false));
    }
  };

  const handleFinalSubmit = () => {
    // The runtime does not validate a 2FA code on payout creation, so there is
    // no confirmation modal — submit directly.
    void submitDispersion().catch(() => {
      /* already handled */
    });
  };

  return {
    resultPhase,
    setResultPhase,
    payoutStatus,
    showResultDetails,
    setShowResultDetails,
    successModalData,
    setSuccessModalData,
    handleFinalSubmit,
    resetIdempotencyKey,
    errorKind,
  };
}
